import { correlationId } from './browser';
const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

// The access token lives only in memory; the refresh token is an httpOnly cookie JS never sees (§10.3).
let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export const setAccessToken = (t: string | null) => void (accessToken = t);
export const setSessionLostHandler = (fn: () => void) => void (onSessionLost = fn);

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details?: Array<{ path: string; message: string }>) {
    super(message);
  }
}

type Opts = { method?: string; body?: unknown; query?: Record<string, string | number | boolean | undefined | null> };

function url(path: string, query?: Opts['query']) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return `${BASE}${path}${s ? `?${s}` : ''}`;
}

/** Single-flight refresh: concurrent 401s share one rotation (a second rotation would look like reuse). */
export function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(url('/admin/v1/auth/refresh'), { method: 'POST', credentials: 'include' })
    .then(async (r) => {
      if (!r.ok) return false;
      const body = (await r.json()) as { access_token: string };
      accessToken = body.access_token;
      return true;
    })
    .catch(() => false)
    .finally(() => setTimeout(() => (refreshing = null), 0));
  return refreshing;
}

export async function api<T>(path: string, opts: Opts = {}, retried = false): Promise<T> {
  const res = await fetch(url(path, opts.query), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: {
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
      'x-correlation-id': correlationId(),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401 && !retried && !path.startsWith('/admin/v1/auth/')) {
    if (await refreshSession()) return api<T>(path, opts, true);
    accessToken = null;
    onSessionLost?.();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string; details?: ApiError['details'] };
    throw new ApiError(res.status, body.error ?? 'error', body.message ?? res.statusText, body.details);
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  return (type.includes('json') ? res.json() : res.text()) as Promise<T>;
}

